import { AreaUnit } from "./machine";

const PYEONG_TO_M2 = 3.3;

export const toM2 = (value: number, unit: AreaUnit) => {
  let result = value;

  switch (unit) {
    case "평":
      result = value * PYEONG_TO_M2;
      break;
    case "m²":
      result = value;
      break;
    case "ha":
      result = value * 10000; // 1ha = 10,000m²
      break;
    case "10a":
      result = value * 1000; // 10a = 1,000m²
      break;
    default:
      result = value;
  }

  return Number(result.toFixed(3));
};

export const fromM2 = (m2: number, unit: AreaUnit) => {
  if (!m2) return 0;

  let result = m2;

  switch (unit) {
    case "평":
      result = m2 / PYEONG_TO_M2;
      break;
    case "m²":
      result = m2;
      break;
    case "ha":
      result = m2 / 10000;
      break;
    case "10a":
      result = m2 / 1000;
      break;
    default:
      result = m2;
  }

  return Number(result.toFixed(3));
};

/**
 * kg/{fromUnit} → kg/{toUnit} 으로 변환
 *
 * 예)
 *  - 1kg/평 → ? kg/m²
 *  - 2.5kg/10a → ? kg/ha
 */
export const convertQuantityUnit = (
  qtyPerFromUnit: number,
  fromUnit: AreaUnit,
  toUnit: AreaUnit
) => {
  if (!qtyPerFromUnit || qtyPerFromUnit <= 0) return 0;
  if (fromUnit === toUnit) return Number(qtyPerFromUnit.toFixed(2));

  // 1 fromUnit 이 몇 m²인지
  const oneFromInM2 = toM2(1, fromUnit);

  // kg/m² 로 환산
  const kgPerM2 = qtyPerFromUnit / oneFromInM2;

  // 1 toUnit 이 몇 m²인지
  const oneToInM2 = toM2(1, toUnit);

  // 다시 단위 기준으로 환산 → kg/{toUnit}
  const kgPerToUnit = kgPerM2 * oneToInM2;

  return Number(kgPerToUnit.toFixed(2));
};

/**
 * 원/{fromUnit} → 원/{toUnit} 으로 변환
 *
 * 예)
 *  - 10000원/평 → ? 원/m²
 *  - 200000원/10a → ? 원/ha
 */
export const convertPriceUnit = (
  pricePerFromUnit: number,
  fromUnit: AreaUnit,
  toUnit: AreaUnit
) => {
  if (!pricePerFromUnit || pricePerFromUnit <= 0) return 0;
  if (fromUnit === toUnit) return Number(pricePerFromUnit.toFixed(2));

  // 1 fromUnit = ? m²
  const oneFromInM2 = toM2(1, fromUnit);

  // 원/m² 로 환산
  const wonPerM2 = pricePerFromUnit / oneFromInM2;

  // 1 toUnit = ? m²
  const oneToInM2 = toM2(1, toUnit);

  // 원/{toUnit} 으로 환산
  const wonPerToUnit = wonPerM2 * oneToInM2;

  return Number(wonPerToUnit.toFixed(2));
};

/**
 * 원/{unit} → 원/m² 로 변환
 */
export const convertUnitPriceToPerM2 = (unitPrice: number, unit: AreaUnit) => {
  if (!unitPrice || unitPrice <= 0) return 0;
  console.log(unitPrice);

  // 1 {unit} 이 몇 m² 인지
  const areaPerUnitM2 = toM2(1, unit); // 예: 평 → 3.3, m² → 1, ha → 10000, 10a → 1000

  if (!areaPerUnitM2) return 0;

  // 원/m² = (원/unit) ÷ (m²/unit)
  const pricePerM2 = unitPrice / areaPerUnitM2;

  // 소수점 처리 (원 단위면 보통 반올림)
  return Math.round(pricePerM2);
  // 소수점 2자리까지 보내고 싶으면:
  // return Number(pricePerM2.toFixed(2));
};

export const formatPriceNumber = (n: number) => {
  if (!n || isNaN(n)) return "0";
  const rounded = Math.round(n); // 소수점 반올림
  return rounded.toLocaleString("ko-KR");
};

export const formatManToEokMan = (manValue: number) => {
  if (!manValue || manValue <= 0) return "0원";

  const total = Math.floor(manValue); // 소수점 버림
  const eok = Math.floor(total / 10000); // 1억 = 10000만원
  const man = total % 10000;

  const parts: string[] = [];
  if (eok > 0) parts.push(`${eok.toLocaleString()}억`);
  if (man > 0) parts.push(`${man.toLocaleString()}만원`);

  if (parts.length === 0) return "0원";
  return parts.join(" ");
};

export const formatWonToEokMan = (wonValue: number) => {
  if (!wonValue || wonValue <= 0) return "0원";

  const total = Math.floor(wonValue); // 원 단위 그대로 사용
  const eok = Math.floor(total / 100000000); // 1억 = 100,000,000원
  const man = Math.floor((total % 100000000) / 10000); // 1만원 = 10,000원

  const parts: string[] = [];
  if (eok > 0) parts.push(`${eok.toLocaleString()}억`);
  if (man > 0) parts.push(`${man.toLocaleString()}만원`);

  if (parts.length === 0) return "0원";
  return parts.join(" ");
};

export const formatNumber = (n: any) => {
  if (n == null) return 0;

  // 문자열인 경우 콤마 제거
  const v = String(n).replace(/,/g, '');

  // 숫자로 변환
  const num = Number(v);

  return isNaN(num) ? 0 : num;
}